// Web Component Accordion Implementation
// Based on ui-accordion custom elements

const setAttrsElement = (element, attributes) => {
    for (const [key, value] of Object.entries(attributes)) {
      if (value === undefined) {
        element?.removeAttribute(key);
      } else {
        element?.setAttribute(key, value);
      }
    }
  };
  
  // Simple store implementation for accordion state management
  const createStore = (initialState) => {
    let state = initialState;
    const listeners = [];
    
    return {
      getState: () => state,
      setState: (newState) => {
        const prevState = { ...state };
        state = { ...state, ...newState };
        listeners.forEach(listener => listener(state, prevState));
      },
      subscribe: (listener) => {
        listeners.push(listener);
        return () => {
          const index = listeners.indexOf(listener);
          if (index > -1) listeners.splice(index, 1);
        };
      }
    };
  };
  
  class UiAccordion extends HTMLElement {
    constructor() {
      super();
      this.unsubscribe = undefined;
      this.useRootStore = createStore({
        value: [],
        mode: 'multiple',
        collapsible: false,
        disabled: false,
        items: [],
        removeItems: (itemToRemove) => {
          const currentState = this.useRootStore.getState();
          this.useRootStore.setState({
            items: currentState.items.filter(item => item !== itemToRemove)
          });
        },
        addItems: (newItems) => {
          const currentState = this.useRootStore.getState();
          this.useRootStore.setState({
            items: [...currentState.items, ...newItems]
          });
        }
      });
    }
  
    static get observedAttributes() {
      return ['disabled', 'value'];
    }
  
    connectedCallback() {
      const mode = this.getAttribute('mode') || this.useRootStore.getState().mode;
      const attrValue = this.getAttribute('value') || null;
      const defaultValue = mode === 'single' ? attrValue : attrValue?.split(',').map(item => item.trim()) || null;
  
      this.useRootStore.setState({
        value: defaultValue,
        mode: mode,
        collapsible: this.hasAttribute('collapsible'),
        disabled: this.hasAttribute('disabled'),
        items: []
      });
  
      this.unsubscribe = this.useRootStore.subscribe((state) => {
        this.dispatchEvent(
          new CustomEvent('onValueChange', {
            detail: { value: state.value }
          })
        );
      });
  
      this.setAttribute('data-ready', '');
    }
  
    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
      this.useRootStore.setState({ items: [] });
    }
  
    attributeChangedCallback(property, oldValue, newValue) {
      const isReady = this.hasAttribute('data-ready');
      if (!isReady) return;
  
      if (property === 'disabled' && oldValue !== newValue) {
        const isDisabled = newValue !== null;
        this.useRootStore.setState({ disabled: isDisabled });
      }
      
      if (property === 'value' && oldValue !== newValue) {
        if (this.useRootStore.getState().mode === 'single') {
          this.useRootStore.setState({ value: newValue });
        } else {
          const newValues = newValue?.split(',').map(item => item.trim()) || [];
          this.useRootStore.setState({ value: newValues });
        }
      }
    }
  
    getAccordionValue() {
      const { mode, items } = this.useRootStore.getState();
  
      if (mode === 'single') {
        const openItem = items.find(item => item.useItemStore.getState().isOpen);
        return openItem ? openItem.useItemStore.getState().value : null;
      } else {
        const openValues = items
          .filter(item => item.useItemStore.getState().isOpen)
          .map(item => item.useItemStore.getState().value)
          .filter(item => item !== null);
        return openValues.length ? openValues : null;
      }
    }
  
    updateToggledItems() {
      const newValue = this.getAccordionValue();
      this.useRootStore.setState({ value: newValue });
      
      setAttrsElement(this, {
        value: Array.isArray(newValue) ? newValue.join(',') : newValue || undefined
      });
    }
  }
  
  class UiAccordionItem extends HTMLElement {
    constructor() {
      super();
      this.$root = null;
      this.useItemStore = createStore({
        value: null,
        isOpen: false,
        disabled: false
      });
      this.unsubscribe = undefined;
      this.unsubscribeRoot = undefined;
    }
  
    static get observedAttributes() {
      return ['disabled'];
    }
  
    connectedCallback() {
      this.$root = this.closest('ui-accordion');
      if (!this.$root) {
        console.error('ui-accordion-item must be child of ui-accordion');
        return;
      }
  
      const rootState = this.$root.useRootStore.getState();
      const attrValue = this.getAttribute('value') || null;
      const disabled = rootState.disabled || this.hasAttribute('disabled') || this.useItemStore.getState().disabled;
  
      const isDefaultOpenSingle = attrValue !== null && rootState.value === attrValue;
      const isDefaultOpenMultiple = !!rootState.value?.includes(attrValue || '');
      const isDefaultOpen = rootState.mode === 'single' ? isDefaultOpenSingle : isDefaultOpenMultiple;
  
      this.useItemStore.setState({
        value: attrValue,
        isOpen: isDefaultOpen,
        disabled: disabled
      });
  
      this.unsubscribe = this.useItemStore.subscribe((state) => {
        setAttrsElement(this, {
          'data-state': state.isOpen ? 'open' : 'closed',
          'data-disabled': state.disabled ? '' : undefined
        });
      });
  
      this.unsubscribeRoot = this.$root.useRootStore.subscribe((state, oldState) => {
        if (oldState.disabled !== state.disabled) {
          const isItemDisabled = this.hasAttribute('disabled');
          this.useItemStore.setState({ disabled: state.disabled || isItemDisabled });
        }
  
        if (state.value !== oldState.value) {
          const { value, isOpen } = this.useItemStore.getState();
          if (value === null) return;
  
          const setOpen = Array.isArray(state.value) ? 
            !!state.value.find(item => item === value) : 
            state.value === value;
  
          if (setOpen !== isOpen) {
            this.toggle(setOpen);
          }
        }
      });
  
      this.$root.useRootStore.getState().addItems([this]);
      this.setAttribute('data-ready', '');
    }
  
    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
      if (this.unsubscribeRoot) this.unsubscribeRoot();
      this.$root?.useRootStore.getState().removeItems(this);
    }
  
    attributeChangedCallback(property, oldValue, newValue) {
      const isReady = this.hasAttribute('data-ready');
      if (!isReady) return;
  
      if (property === 'disabled' && oldValue !== newValue) {
        const isDisabled = newValue !== null;
        if (this.$root?.useRootStore.getState().disabled) {
          this.useItemStore.setState({ disabled: isDisabled });
        }
      }
    }
  
    toggle(_setOpen) {
      if (this.$root) {
        const { collapsible, items, mode } = this.$root.useRootStore.getState();
        const setOpen = _setOpen ?? !this.useItemStore.getState().isOpen;
  
        if (!collapsible && !setOpen && mode === 'single') return;
  
        this.useItemStore.setState({ isOpen: setOpen });
  
        if (mode === 'single' && setOpen) {
          const filteredItems = items.filter(item => item !== this && item.useItemStore.getState().isOpen);
          filteredItems.forEach(item => {
            item.useItemStore.setState({ isOpen: false });
          });
        }
  
        this.$root.updateToggledItems();
      }
    }
  
    open() {
      this.toggle(true);
    }
  
    close() {
      this.toggle(false);
    }
  }
  
  class UiAccordionTrigger extends HTMLElement {
    constructor() {
      super();
      this.$parentItem = null;
      this.$button = null;
      this.unsubscribe = undefined;
      this.handleClick = this.handleClick.bind(this);
    }
  
    connectedCallback() {
      this.$parentItem = this.closest('ui-accordion-item');
      if (!this.$parentItem) {
        console.error('ui-accordion-trigger must be child of ui-accordion-item');
        return;
      }
  
      this.$button = this.querySelector('button:not(:scope ui-accordion *)');
      const $content = this.$parentItem.querySelector('ui-accordion-content:not(:scope ui-accordion *)');
      const triggerId = this.$button?.id || `accordion-trigger-${Math.random().toString(36).slice(2)}`;
      const contentId = $content?.id || `accordion-content-${Math.random().toString(36).slice(2)}`;
      const { isOpen, disabled } = this.$parentItem.useItemStore.getState();
  
      this.updateAttrs(isOpen, disabled, triggerId, contentId);
      this.unsubscribe = this.$parentItem.useItemStore.subscribe((state) => {
        this.updateAttrs(state.isOpen, state.disabled, triggerId, contentId);
      });
      this.$button?.addEventListener('click', this.handleClick);
    }
  
    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
      this.$button?.removeEventListener('click', this.handleClick);
    }
  
    updateAttrs(isOpen, isDisabled, triggerId, contentId) {
      setAttrsElement(this, {
        'data-state': isOpen ? 'open' : 'closed',
        'data-disabled': isDisabled ? '' : undefined
      });
      setAttrsElement(this.$button, {
        'data-state': isOpen ? 'open' : 'closed',
        'data-disabled': isDisabled ? '' : undefined,
        disabled: isDisabled ? '' : undefined,
        'aria-expanded': isOpen ? 'true' : 'false',
        'aria-controls': contentId,
        id: triggerId
      });
    }
  
    handleClick() {
      this.$parentItem?.toggle();
    }
  }
  
  class UiAccordionHeader extends HTMLElement {
    constructor() {
      super();
      this.$parentItem = null;
      this.unsubscribe = undefined;
    }
  
    connectedCallback() {
      this.$parentItem = this.closest('ui-accordion-item');
      if (!this.$parentItem) {
        console.error('ui-accordion-header must be child of ui-accordion-item');
        return;
      }
  
      const level = '3';
      const role = this.getAttribute('role');
      if (!role) {
        this.setAttribute('role', 'heading');
      }
      if (this.getAttribute('role') === 'heading') {
        const ariaLevel = this.getAttribute('level') || this.getAttribute('aria-level') || level;
        this.setAttribute('aria-level', ariaLevel);
      }
  
      const { isOpen, disabled } = this.$parentItem.useItemStore.getState();
      this.updateAttrs(isOpen, disabled);
  
      this.unsubscribe = this.$parentItem.useItemStore.subscribe((state) => {
        this.updateAttrs(state.isOpen, state.disabled);
      });
    }
  
    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }
  
    updateAttrs(isOpen, isDisabled) {
      setAttrsElement(this, {
        'data-state': isOpen ? 'open' : 'closed',
        'data-disabled': isDisabled ? '' : undefined
      });
    }
  }
  
  class UiAccordionContent extends HTMLElement {
    constructor() {
      super();
      this.$parentItem = null;
      this.unsubscribe = undefined;
    }
  
    connectedCallback() {
      this.$parentItem = this.closest('ui-accordion-item');
      if (!this.$parentItem) {
        console.error('ui-accordion-content must be child of ui-accordion-item');
        return;
      }
  
      const { isOpen, disabled } = this.$parentItem.useItemStore.getState();
      const $button = this.$parentItem.querySelector('ui-accordion-trigger button');
      const triggerId = $button?.id;
      const contentId = this.id || $button?.getAttribute('aria-controls') || `accordion-content-${Math.random().toString(36).slice(2)}`;
      let isTransitioning = false;
  
      this.updateAttrs(isOpen, disabled, triggerId, contentId);
  
      const handleTransitionRun = () => {
        this.removeEventListener('transitionrun', handleTransitionRun);
        isTransitioning = true;
      };
      
      const handleTransitionEnd = () => {
        this.removeEventListener('transitionend', handleTransitionEnd);
        isTransitioning = false;
  
        this.removeAttribute('data-ending-style');
        this.style.removeProperty('--accordion-content-height');
  
        if (this.$parentItem) {
          const { isOpen, disabled } = this.$parentItem.useItemStore.getState();
          if (!isOpen) {
            this.updateAttrs(false, disabled, triggerId, contentId);
          }
        }
      };
  
      this.unsubscribe = this.$parentItem.useItemStore.subscribe((state) => {
        const hasTransition = window.getComputedStyle(this).transitionDuration !== '0s';
  
        if (!hasTransition) {
          this.updateAttrs(state.isOpen, state.disabled, triggerId, contentId);
          return;
        }
  
        if (isTransitioning) {
          this.removeAttribute('data-ending-style');
  
          if (state.isOpen) {
            this.updateAttrs(true, state.disabled, triggerId, contentId);
            this.style.setProperty('--accordion-content-height', `${this.scrollHeight}px`);
          } else {
            this.style.setProperty('--accordion-content-height', `0px`);
          }
        } else {
          this.style.removeProperty('--accordion-content-height');
  
          if (state.isOpen) {
            this.updateAttrs(true, state.disabled, triggerId, contentId);
            this.setAttribute('data-starting-style', '');
            this.removeAttribute('data-ending-style');
            this.style.setProperty('--accordion-content-height', `${this.scrollHeight}px`);
  
            this.addEventListener('transitionrun', handleTransitionRun);
            this.addEventListener('transitionend', handleTransitionEnd);
  
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                this.removeAttribute('data-starting-style');
              });
            });
          } else {
            this.style.setProperty('--accordion-content-height', `${this.scrollHeight}px`);
  
            this.addEventListener('transitionrun', handleTransitionRun);
            this.addEventListener('transitionend', handleTransitionEnd);
  
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                this.setAttribute('data-ending-style', '');
              });
            });
          }
        }
      });
    }
  
    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }
  
    updateAttrs(isOpen, isDisabled, triggerId, contentId) {
      setAttrsElement(this, {
        'data-state': isOpen ? 'open' : 'closed',
        'data-disabled': isDisabled ? '' : undefined,
        hidden: !isOpen ? 'until-found' : undefined,
        role: 'region',
        'aria-labelledby': triggerId,
        id: contentId || undefined
      });
    }
  }
  
  // Define custom elements
  customElements.define('ui-accordion', UiAccordion);
  customElements.define('ui-accordion-item', UiAccordionItem);
  customElements.define('ui-accordion-header', UiAccordionHeader);
  customElements.define('ui-accordion-trigger', UiAccordionTrigger);
  customElements.define('ui-accordion-content', UiAccordionContent);